/**
 * メッセージバブルコンポーネント
 *
 * ユーザーとAIのメッセージを吹き出し形式で表示
 */

import { Box, Group, Paper, Text, Badge } from "@mantine/core";
import { User, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Message } from "../../services/assistantService";

interface MessageBubbleProps {
  message: Message;
}

/**
 * メッセージバブル
 */
export function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === "user";

  // 添付画像のレンダリング
  const renderImages = () => {
    if (!message.imageUrls || message.imageUrls.length === 0) return null;
    return (
      <Group gap="xs" mb="xs">
        {message.imageUrls.map((url, idx) => (
          <Box
            key={idx}
            style={{
              width: 80,
              height: 80,
              borderRadius: 8,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            <img
              src={url}
              alt={`添付画像 ${idx + 1}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
        ))}
      </Group>
    );
  };

  // ユーザーメッセージ: 吹き出し形式
  if (isUser) {
    return (
      <Group
        align="flex-start"
        gap="sm"
        style={{
          flexDirection: "row-reverse",
          paddingLeft: "2rem",
        }}
      >
        <Box
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: "var(--mantine-color-blue-6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <User size={18} color="white" />
        </Box>
        <Paper
          p="sm"
          radius="lg"
          style={{
            backgroundColor: "var(--mantine-color-blue-6)",
            color: "white",
            maxWidth: "85%",
          }}
        >
          {renderImages()}
          <Text size="sm" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
            {message.content}
          </Text>
          {message.hasImages && !message.imageUrls && (
            <Badge size="xs" color="gray" mt="xs" variant="light">
              画像添付
            </Badge>
          )}
        </Paper>
      </Group>
    );
  }

  // AIメッセージ: アイコン＋Markdown（吹き出しなし）
  return (
    <Group
      align="flex-start"
      gap="sm"
      style={{ paddingRight: "1rem" }}
      wrap="nowrap"
    >
      <Sparkles
        size={20}
        strokeWidth={1.5}
        color="var(--mantine-color-blue-6)"
        style={{ flexShrink: 0, marginTop: 2 }}
      />
      <Box style={{ flex: 1, overflow: "hidden" }} className="markdown-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // 内部リンクはReact Routerで遷移（フルページリロード防止）
            a: ({ href, children, ...props }) => {
              // 内部リンク判定（相対パス or 同一オリジン）
              const isInternal =
                href &&
                (href.startsWith("/") ||
                  href.startsWith("?") ||
                  href.startsWith(window.location.origin));
              if (isInternal && href) {
                // 同一オリジンの場合は相対パスに変換
                const path = href.startsWith(window.location.origin)
                  ? href.replace(window.location.origin, "")
                  : href;
                return (
                  <Link
                    to={path}
                    style={{ color: "var(--mantine-color-blue-6)" }}
                  >
                    {children}
                  </Link>
                );
              }
              // 外部リンクは通常のaタグ
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--mantine-color-blue-6)" }}
                  {...props}
                >
                  {children}
                </a>
              );
            },
          }}
        >
          {message.content}
        </ReactMarkdown>
      </Box>
    </Group>
  );
}

/**
 * 入力中のインジケーター
 */
export function TypingIndicator() {
  return (
    <Group align="flex-start" gap="sm" style={{ paddingRight: "1rem" }}>
      <Sparkles
        size={20}
        strokeWidth={1.5}
        color="var(--mantine-color-blue-6)"
        style={{ flexShrink: 0, marginTop: 2 }}
      />
      <Group gap={6} align="center">
        <Box
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: "var(--mantine-color-blue-6)",
            animation: "pulse 1.4s ease-in-out infinite",
          }}
        />
        <Text size="sm" c="dimmed">
          考え中...
        </Text>
      </Group>
    </Group>
  );
}
